import { CloseRounded } from "@mui/icons-material";
import { Button, CircularProgress, Grid, IconButton, styled, Typography } from "@mui/material";

import { Field, Form, Formik, type FormikProps } from "formik";
import { TextField } from "formik-mui";
import { useSnackbar } from "notistack";
import { Suspense, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useCaseShare, useCaseShareRevoke, type CaseShareMutationData } from "src/hooks/caseHooks";
import { useCase } from "src/hooks/useCase";
import { useCaseShareSchema } from "src/schemas/case-share.schema";
import { parseServerError } from "src/utils/string.utils";

interface Props {
  caseId: number;
  onClose: () => void;
}

export function CaseShareForm(props: Props) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<CircularProgress size={20} />}>
        <DataLoader {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function DataLoader(props: Props) {
  const { t } = useTranslation();
  const { data: caseData } = useCase(props.caseId);

  const formRef = useRef<FormikProps<CaseShareMutationData>>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: shareCase, isLoading, error } = useCaseShare(props.caseId);
  const { mutateAsync: revokeCase, isLoading: isRevoking, error: revokeError } = useCaseShareRevoke(props.caseId);
  const validationSchema = useCaseShareSchema();
  const initialValues: CaseShareMutationData = {
    email: "",
  };

  async function onSubmit(values: CaseShareMutationData) {
    await shareCase(values.email, {
      onError: (e) => {
        const error = e.response?.data[0];
        if (error) {
          enqueueSnackbar(parseServerError(error), {
            variant: "error",
          });
        } else {
          enqueueSnackbar(t("label.somethingWentWrong"), {
            variant: "error",
          });
        }
      },
      onSettled: () => {
        formRef.current?.resetForm();
      },
    });
  }
  async function onRevoke(email?: string) {
    await revokeCase({ email }, {
      onError: (e) => {
        const error = e.response?.data[0];
        if (error) {
          enqueueSnackbar(parseServerError(error), {
            variant: "error",
          });
        } else {
          enqueueSnackbar(t("label.somethingWentWrong"), {
            variant: "error",
          });
        }
      },
      onSettled: () => {
        formRef.current?.resetForm();
      },
    });
  }

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
    >
      {({ isSubmitting }) => (
        <Styles>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Field component={TextField} name="email" label={t("label.email")} type="text" fullWidth autoFocus />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <ServerErrors data={error || revokeError} />
              </Grid>
            )}
            <Grid container item xs={12}>
              <Grid item xs={6}>
                <Typography variant="body1" display="flex" alignItems="center" gap={2}>
                  {t("label.sharedWith")} ({caseData?.sharedEmailList.length})
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <ButtonLoading
                  isLoading={isRevoking}
                  type="button"
                  variant="text"
                  disabled={caseData?.sharedEmailList.length === 0 || isSubmitting}
                  onClick={() => onRevoke(undefined)}
                  className="revoke-all"
                  color="error"
                >
                  {t("label.revokeAll")}
                </ButtonLoading>
              </Grid>
            </Grid>
            <Grid container item xs={12} className="shared-with-list">
              {caseData?.sharedEmailList.map((email) => (
                <Grid item xs={12} key={email} className="shared-with-item">
                  <Typography variant="body1">{email}</Typography>
                  <IconButton
                    type="button"
                    onClick={() => onRevoke(email)}
                    className="revoke"
                    size="small"
                    disabled={isRevoking || isSubmitting}
                  >
                    {isRevoking || isSubmitting ? <CircularProgress size={16} /> : <CloseRounded fontSize="small" />}
                  </IconButton>
                </Grid>
              ))}
            </Grid>
            <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" onClick={props.onClose}>
                {t("label.close")}
              </Button>
              <ButtonLoading
                isLoading={isSubmitting || isLoading || isRevoking}
                type="submit"
                color="primary"
                variant="contained"
              >
                {t("label.share")}
              </ButtonLoading>
            </ButtonGrid>
          </Grid>
        </Styles>
      )}
    </Formik>
  );
}

const Styles = styled(Form)`
  .revoke-all {
    color: ${({ theme }) => theme.palette.error.main};
    margin-left: auto;
  }

  .revoke {
    .MuiCircularProgress-root {
      display: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .shared-with-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    max-height: 50px;
    height: 100%;
  }
  .MuiGrid-item.shared-with-list {
    padding-top: ${({ theme }) => theme.spacing(0.5)};
    > * {
      :first-of-type {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
      }
      :last-of-type {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
      }
      padding: ${({ theme }) => theme.spacing(1.5)};
      background-color: #fafafa;
    }
  }
`;
